import type { HomeItem } from './types';

/**
 * Фикстура блоков Главной — порядок и тексты verbatim из canonical spike
 * `docs/prototypes/_design_spike/screen-home-spike.html` (CARDS array).
 *
 * Структура: 14 блоков + 1 текстовый разделитель «— дополнительные сервисы —»
 * между id=9 и id=10. Маппинг id → prdBlockNumber фиксирован константой массива.
 * PR-H5a добавил id=14 «Коммерческое предложение» (второй вход в Block 12,
 * prdBlockNumber=12, entry='quote') сразу после id=8.
 *
 * v0.4 (ADR-0023): `customIcon` field удалён (cards теперь text-only без иконок).
 * v0.4.1 (H2.3): `featured` field удалён — Видеоинструкция (id=1) рендерится через
 * dedicated `<VideoButton>` component в HomeView, поэтому id=1 entry оставлен как
 * метаданные (preserve id→prdBlockNumber mapping), но в render UI скипается.
 *
 * Политика `available`:
 * - true для prdBlockNumber 2/3/4 (Декор / Краска / Ручки) — реализованы.
 * - true для prdBlockNumber 6 (Конфигуратор) — Block 6 Phase 1 stub-уровень
 *   (ADR-0015): TypePickerScreen полнофункционален, ConfiguratorScreen — stub
 *   с placeholder body, но навигация работает. Phase 2 расширит до SVG.
 * - true для prdBlockNumber 12 (Калькулятор) — Block 12 PR-A Phase A6+A7:
 *   `/calculator` route с CalculatorEntry feature (Экран 2.12.1). Карточка
 *   визуально «доступна» на Главной. paywallGate=true остаётся — Home.tsx
 *   `handleBlockClick` проверяет paywallGate ДО !available, поэтому гейт
 *   срабатывает первым для grace/expired_readonly/archived → PaywallModal,
 *   а для trial/paid — navigate inline на /calculator (см. Home.tsx line 98).
 * - true для prdBlockNumber 11 (Личный кабинет) — C6 (2026-06-18): хаб 2.11.1
 *   `/cabinet` (Phase 11.A) доступен; карточка навигирует по href, paywallGate=false.
 * - false для остальных (id=1 Видео, id=7 Технолог, id=11 Сотрудничество,
 *   id=12 Что улучшить) — клик показывает модалку «Функция в разработке».
 *
 * Политика `paywallGate`:
 * - true для prdBlockNumber 12/13 (Калькулятор / Договор) — клик попадает в
 *   paywall-ветку Home.tsx handleBlockClick. Real subscription state machine
 *   (trial/paid/grace/expired_readonly/archived) — Block 0 Phase 4 ADR-0014.
 */
export const HOME_BLOCKS = [
  {
    type: 'block',
    id: 1,
    prdBlockNumber: 1,
    title: 'Видеоинструкция',
    desc: '',
    available: false,
    paywallGate: false,
  },
  {
    type: 'block',
    id: 2,
    prdBlockNumber: 2,
    title: 'Подбери декор и найди магазины',
    desc: 'ЛДСП, Столешницы, Плёнка, Камень',
    available: true,
    paywallGate: false,
    href: '/decors',
  },
  {
    type: 'block',
    id: 3,
    prdBlockNumber: 3,
    title: 'Подбери цвет краски фасадов',
    desc: 'RAL по фото или каталогу + магазины',
    available: true,
    paywallGate: false,
    href: '/paint',
  },
  {
    type: 'block',
    id: 4,
    prdBlockNumber: 4,
    title: 'Подбери ручки и крючки, найди в магазинах',
    desc: 'Ручки и крючки, найди в магазинах',
    available: true,
    paywallGate: false,
    href: '/handles',
  },
  {
    type: 'block',
    id: 5,
    prdBlockNumber: 5,
    title: 'Умный фурнитурщик',
    desc: 'Опиши проблему — получи решение и нужную фурнитуру',
    available: true,
    paywallGate: false,
    href: '/furniture',
  },
  {
    type: 'block',
    id: 6,
    prdBlockNumber: 6,
    title: 'Собери лучшее сочетание декоров',
    desc: 'Фасад + столешница + ручки на мебели',
    available: true,
    paywallGate: false,
    href: '/configurator',
  },
  {
    type: 'block',
    id: 7,
    prdBlockNumber: 7,
    title: 'Умный технолог',
    desc: 'Опиши проблему — получи решение',
    available: false,
    paywallGate: false,
  },
  {
    type: 'block',
    id: 8,
    prdBlockNumber: 12,
    title: 'Рассчитать стоимость мебели',
    desc: 'Смета + коммерческое предложение для клиента',
    available: true,
    paywallGate: true,
  },
  // PR-H5a: второй вход в Block 12 (prdBlockNumber=12) — Коммерческое предложение.
  // Дискриминатор `entry` различает две точки входа с одинаковым prdBlockNumber:
  //   id=8  (entry отсутствует → default) → navigate('/calculator');
  //   id=14 (entry='quote')               → открывает QuoteEntryPicker (модалка
  //                                          выбора «создать новый / открыть КП»).
  // См. Home.tsx handleBlockClick (ветка prdBlockNumber === 12).
  {
    type: 'block',
    id: 14,
    prdBlockNumber: 12,
    title: 'Коммерческое предложение',
    desc: 'Создать новое либо открыть сохранённое',
    available: true,
    paywallGate: true,
    entry: 'quote',
  },
  {
    type: 'block',
    id: 9,
    prdBlockNumber: 13,
    title: 'Договор на мебель',
    desc: 'Договор со спецификацией',
    available: true,
    paywallGate: true,
  },
  // Block 14 «Раскрой» (PR #2-UI) — после «Договор на мебель», в основной группе
  // (НЕ в «доп. сервисы»), решение владельца 07.06.2026. Бесплатный (§IX.X #2) —
  // paywallGate=false ОБЯЗАТЕЛЬНО. Без подзаголовка (desc='') — решение владельца.
  {
    type: 'block',
    id: 15,
    prdBlockNumber: 14,
    title: 'Раскрой ЛДСП, МДФ, ХДФ',
    desc: '',
    available: true,
    paywallGate: false,
    href: '/cutting',
  },
  { type: 'sep', label: '— дополнительные сервисы —' },
  // Block 8 «Партнёры» (Фаза F1) — каталог подрядчиков включён на Главной.
  // available=true + href='/partners' → клик ведёт на экран категорий 2.8.1
  // (PartnersCategories, ADR-0080). Бесплатный доп.сервис (paywallGate=false).
  {
    type: 'block',
    id: 10,
    prdBlockNumber: 8,
    title: 'Партнёры (распил, фасады и др.)',
    desc: 'Контакты подрядчиков',
    available: true,
    paywallGate: false,
    href: '/partners',
  },
  {
    type: 'block',
    id: 11,
    prdBlockNumber: 9,
    title: 'Предложения для сотрудничества',
    desc: 'Заявка стать партнером',
    available: false,
    paywallGate: false,
  },
  {
    type: 'block',
    id: 12,
    prdBlockNumber: 10,
    title: 'Что улучшить в сервисе?',
    desc: 'Напиши что улучшить и мы улучшим',
    available: false,
    paywallGate: false,
  },
  // C6 (2026-06-18): Личный кабинет доступен — хаб 2.11.1 `/cabinet` (Phase 11.A)
  // зарегистрирован и работает. available=true + href='/cabinet' (паттерн id=2/3/4 —
  // навигация по href). paywallGate=false: кабинет бесплатен, Главная — для
  // залогиненного пользователя. desc «Профиль, подписка, сообщения» — синхрон с
  // хабом ЛК (id=13 «Мои проекты» убран из хаба в 11.A).
  {
    type: 'block',
    id: 13,
    prdBlockNumber: 11,
    title: 'Личный кабинет',
    desc: 'Профиль, подписка, сообщения',
    available: true,
    paywallGate: false,
    href: '/cabinet',
  },
] as const satisfies readonly HomeItem[];
